import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Wallet, Coins, ShoppingBag, ArrowRight, Lock } from 'lucide-react';
import api from '../utils/api';

const TSBMiniWidget = () => {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
    const id = setInterval(load, 45000);
    return () => clearInterval(id);
  }, []);

  const load = async () => {
    try {
      const [w, s] = await Promise.all([
        api.get('/tsb/wallet/me'),
        api.get('/tsb/settings'),
      ]);
      setData(w.data.wallet);
      setSettings(s.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) return null;

  const cash = data.cash_balance || 0;
  const tsb = data.tsb_balance || 0;
  const rate = settings?.current_tsb_rate || 1;
  const netWorth = cash + tsb * rate;

  return (
    <div
      className="relative overflow-hidden rounded-2xl border-2 border-amber-500/30 bg-slate-900 p-4 sm:p-5 mb-6"
      data-testid="tsb-mini-widget"
    >
      <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
        <span className="text-[9px] font-bold bg-amber-500/30 text-amber-200 px-2 py-0.5 rounded-full">LOYALTY</span>
        <span className="text-[9px] font-bold bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded-full flex items-center gap-1">
          <Lock className="w-2.5 h-2.5" /> CLOSED-LOOP
        </span>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <Coins className="w-5 h-5 text-amber-400" />
        <h3 className="font-bold text-base sm:text-lg bg-gradient-to-r from-amber-300 to-orange-300 bg-clip-text text-transparent">
          TSB Rewards — Your Wallet
        </h3>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <Stat
          label="Cash Balance"
          value={`₹${cash.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
          color="text-emerald-300"
          icon={<Wallet className="w-3.5 h-3.5" />}
          testid="tsb-widget-cash"
        />
        <Stat
          label="TSB Points"
          value={tsb.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          color="text-amber-300"
          icon={<Coins className="w-3.5 h-3.5" />}
          testid="tsb-widget-points"
        />
        <Stat
          label="Net Worth"
          value={`₹${netWorth.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
          color="text-yellow-300"
          testid="tsb-widget-networth"
        />
        <Stat
          label="Rate"
          value={`₹${rate}`}
          color="text-purple-300"
          testid="tsb-widget-rate"
        />
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[11px] text-slate-400 flex items-center gap-1">
          <Lock className="w-3 h-3" /> Cash → TSB only. 50/50 checkout on Marketplace.
        </div>
        <Link
          to="/tsb"
          data-testid="tsb-widget-open"
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-semibold rounded-lg text-sm hover:scale-105 transition"
        >
          <ShoppingBag className="w-3.5 h-3.5" />
          Open TSB
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
};

const Stat = ({ label, value, color, icon, testid }) => (
  <div className="bg-slate-900/40 rounded-lg p-2 border border-slate-700/50" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1">
      {icon}{label}
    </div>
    <div className={`text-sm sm:text-base font-bold mt-0.5 ${color}`}>{value}</div>
  </div>
);

export default TSBMiniWidget;
